import { useCallback, useEffect, useMemo, useState } from 'react';

import { FormErrorMessage } from '@bambi/component-library/src/components/forms/FormErrorMessage/FormErrorMessage';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { createColumnHelper } from '@tanstack/react-table';

import { DataGrid } from 'src/common/DataGrid/DataGrid';
import {
  PayerPassengerRead,
  useLazyPayersPassengersRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { SectionHeading } from 'src/common/primitives/Section/SectionHeading';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { PayerPassengerManageCell } from './PayerPassengerManageCell';

const columnHelper = createColumnHelper<PayerPassengerRead>();

interface PayerPassengerDataGridProps {
  payerPassengerIds: string[];
  passengerId: string;
}
export function PayerPassengerDataGrid({
  payerPassengerIds,
  passengerId,
}: PayerPassengerDataGridProps) {
  const [fetchPayerPassenger] = useLazyPayersPassengersRetrieveQuery();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payerPassengers, setPayerPassengers] = useState<PayerPassengerRead[]>(
    []
  );

  const fetchAllPayerPassengers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payerPassengers = await Promise.all(
        payerPassengerIds.map((payerPassengerId) =>
          fetchPayerPassenger({
            payerOrganizationId: payerPassengerId,
            passengerId,
          }).unwrap()
        )
      );
      setPayerPassengers(payerPassengers);
    } catch (error) {
      setError(formatServerError(error));
    } finally {
      setLoading(false);
    }
  }, [fetchPayerPassenger, payerPassengerIds, passengerId]);

  useEffect(() => {
    fetchAllPayerPassengers();
  }, [fetchAllPayerPassengers]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('payer_organization.display_name', {
        header: 'Payer',
      }),
      columnHelper.accessor('external_id', {
        header: 'External ID',
      }),
      columnHelper.accessor('authorization_id', {
        header: 'Authorization ID',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Manage',
        cell: ({ row }) => (
          <PayerPassengerManageCell
            payerPassenger={row.original}
            onAfterSave={fetchAllPayerPassengers}
          />
        ),
      }),
    ],
    [fetchAllPayerPassengers]
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <FormErrorMessage>{error}</FormErrorMessage>;
  }

  if (payerPassengers.length === 0) {
    return null;
  }

  return (
    <>
      <hr className="mt-6 mb-4 border-gray-200" />
      <SectionHeading heading="Payer Passenger Information" />
      <DataGrid<PayerPassengerRead>
        tableId="payer-passenger"
        data={payerPassengers}
        columns={columns}
        includePagination={false}
      />
    </>
  );
}
